import { Toast } from "components/toast";
import apiDigitacao from "api/api-digitacao";
import {
  Attendance,
  Convenio
} from "components/atendimentos-components/atendimento-form/types";
import { useAttendanceContext } from "contexts/attendance-context";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ModalDigitarPropostaFGTS } from "../modal-digitar-proposta";
import { SkeletonLoading } from "components/skeleton-loading";
import { ModalAcompanharProposta } from "./modal-acompanhar-proposta";
import { ModalDigitacaoINSS } from "../../margens/modal-digitacao-inss";
import { AttendanceTabKey } from "components/atendimentos-components/pagina-atendimento/attendance-data-form";
import { ModalConsultarProposta } from "./modal-consultar-proposta";
import { ModalDigitacaoCartao } from "../../margens/modal-digitacao-cartao";
import { ModalDigitacaoSaque } from "../../contratos/modal-digitacao-saque";
import { DigitadoCard } from "./card-digitado";
import { ModalDigitacaoConsigCLT } from "../../margens/emprestimo-consignado-clt/modal-digitacao-consig-clt";

export interface DigitadoRow {
  convenio: Convenio;
  banco: string;
  ade: string;
  lastStatusADE?: string;
  linkFormalizacao: string;
  dataDigitacao: string;
  id: number;
  status: "PENDING" | "FINISHED" | "SYSTEM_ERROR" | null;
  messageDigitacao: string | null;
  codeRequestDigitacao: "SUCCESS" | "SYSTEM_ERROR" | null;
  isLoading?: boolean; // Campo da interface (não vem no backend)
  stormContratoFF: string | null;
  valorLiquidoCliente: number;
  operacao: string;
  valorLimiteCartao?: number;
}

export const tiposOperacaoDigitados = [
  { name: "Cartão Novo RMC", value: "CARTAO_NOVO_RMC" },
  { name: "Cartão Novo RCC", value: "CARTAO_NOVO_RCC" },
  {
    name: "Saque Complementar RCC",
    value: "SAQUE_COMPLEMENTAR_RCC"
  },
  {
    name: "Saque Complementar RMC",
    value: "SAQUE_COMPLEMENTAR_RMC"
  }
];

export function DigitadosAtendimentoPart({
  isLoading,
  setIsLoading,
  onChangeTab,
  telaAtendimento
}: {
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  onChangeTab: (tabKey: AttendanceTabKey) => void;
  telaAtendimento: boolean;
}) {
  const [loadingTabela, setLoadingTabela] = useState(false);
  const [emptyMessage, setEmptyMessage] = useState("");
  const [digitadosList, setDigitadosList] = useState<DigitadoRow[]>([]);
  const { formControl } = useAttendanceContext();
  const attendance = formControl.values as Attendance;

  const getDigitados = async () => {
    const url = ["INSS", "GOV", "PREF", "SIAPE"].includes(attendance.convenio!)
      ? `/api/digitacoes/inss?convenio=${attendance.convenio}`
      : `/api/digitacoes`;
    setLoadingTabela(true);
    try {
      const { data } = await apiDigitacao.get(url, {
        params: {
          atendimento_id: attendance.id
        }
      });
      if (!data.length) {
        setEmptyMessage("Não há digitações");
      } else setEmptyMessage("");
      setDigitadosList(data);
    } catch (e) {
      Toast({ title: "Erro ao obter lista", status: "error" });
    } finally {
      setLoadingTabela(false);
    }
  };

  useEffect(() => {
    getDigitados();
  }, []);

  const consultarProposta = async (
    row: DigitadoRow,
    onChange: (k: keyof DigitadoRow | null, v: any) => void
  ) => {
    onChange("isLoading", true);
    try {
      const { data } = await apiDigitacao.get(
        `/api/digitacoes/fgts/consultar-proposta`,
        { params: { digitacao_id: row.id } }
      );

      if (data.status === "PENDING") {
        Toast({ title: "Digitação em andamento", status: "info" });
      } else if (
        data.status === "FINISHED" &&
        data.codeRequestDigitacao === "SUCCESS"
      ) {
        Toast({ title: "Consultado com sucesso", status: "success" });
      }
      onChange(null, data);
    } catch (e) {
      Toast({ title: "Erro ao consultar", status: "error" });
    } finally {
      onChange("isLoading", false);
    }
  };

  const deleteDigitacaoFGTS = async ({
    onChange,
    index,
    row
  }: {
    onChange: (k: keyof DigitadoRow | null, v: any) => void;
    index: number;
    row: DigitadoRow;
  }) => {
    onChange("isLoading", true);
    try {
      const { data } = await apiDigitacao.delete(
        `/api/digitacoes/fgts/${row.id}`
      );
      setDigitadosList((prev) => prev.filter((r, i) => i !== index));
    } catch (e) {
      Toast({ title: "Erro ao excluir digitação" });
    } finally {
      onChange("isLoading", false);
    }
  };

  const deleteDigitacaoINSS = async ({
    onChange,
    index,
    row
  }: {
    onChange: (k: keyof DigitadoRow | null, v: any) => void;
    index: number;
    row: DigitadoRow;
  }) => {
    onChange("isLoading", true);
    try {
      const { data } = await apiDigitacao.delete(
        `/api/digitacoes/inss/${row.id}`
      );
      setDigitadosList((prev) => prev.filter((r, i) => i !== index));
    } catch (e) {
      Toast({ title: "Erro ao excluir digitação" });
    } finally {
      onChange("isLoading", false);
    }
  };

  return (
    <>
      {loadingTabela ? (
        <SkeletonLoading isLoading w="100%" h="192px" borderRadius="10px" />
      ) : (
        <>
          {emptyMessage}
          {digitadosList.map((row, index, arr) => {
            const isLast = index + 1 === arr.length;
            const onChange = (k: keyof DigitadoRow | null, v: any) =>
              setDigitadosList((prev) =>
                prev.map((r, i) =>
                  i === index ? (k == null ? v : { ...r, [k]: v }) : r
                )
              );
            const allDisabledDigitacaoINSS =
              ["C6", "FACTA"].includes(row.banco) &&
              row.convenio !== "CONSIGNADO_TRABALHADOR"
                ? row.codeRequestDigitacao !== "SYSTEM_ERROR"
                : true;

            const isVisibleAcompanharProposta = [
              "ANTECIPACAO_FGTS",
              "INSS",
              "GOV",
              "PREF",
              "SIAPE",
              "CONSIGNADO_TRABALHADOR"
            ].includes(row.convenio!);

            const isVisibleConsultarProposta = [
              "ANTECIPACAO_FGTS",
              "INSS",
              "GOV",
              "PREF",
              "SIAPE",
              "CONSIGNADO_TRABALHADOR"
            ].includes(row.convenio!);

            const digitacaoCartao =
              row.operacao === "CARTAO_NOVO_RMC" ||
              row.operacao === "CARTAO_NOVO_RCC";

            return (
              <DigitadoCard
                allDisabledDigitacaoINSS={allDisabledDigitacaoINSS}
                consultarProposta={consultarProposta}
                deleteDigitacaoFGTS={deleteDigitacaoFGTS}
                deleteDigitacaoINSS={deleteDigitacaoINSS}
                digitacaoCartao={digitacaoCartao}
                index={index}
                isLast={isLast}
                isVisibleAcompanharProposta={isVisibleAcompanharProposta}
                isVisibleConsultarProposta={isVisibleConsultarProposta}
                onChange={onChange}
                row={row}
                telaAtendimento={telaAtendimento}
                key={index}
                digitadosList={digitadosList}
              />
            );
          })}
        </>
      )}

      <ModalDigitarPropostaFGTS />
      <ModalDigitacaoINSS />
      <ModalDigitacaoCartao />
      <ModalDigitacaoSaque />

      <ModalConsultarProposta attendance={attendance} />
      <ModalAcompanharProposta attendance={attendance} />
      <ModalDigitacaoConsigCLT />
    </>
  );
}
